'use client';

/**
 * Form atoms for customer-facing pages.
 *
 * The pages these serve were built from bare `<input className="border p-2">`
 * elements with placeholder-only labelling , which fails on three counts: the
 * label vanishes the moment the guest types, screen readers get nothing, and
 * the 36px-ish hit area is below the 44px touch minimum. These wrap the shared
 * Input primitive with a real <label>, an id association, inline error text
 * tied by aria-describedby, and comfortable target sizes.
 */

import { type ReactNode, useId } from 'react';
import { Check, type LucideIcon } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/components/ui/utils';

export function CustomerField({
  label,
  hint,
  error,
  optional,
  trailing,
  children,
  className,
  fieldClassName,
  ...inputProps
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  optional?: boolean;
  /** Rendered beside the input , e.g. a "Max" button on a points field. */
  trailing?: ReactNode;
  /** Supply a custom control instead of the default Input. */
  children?: (props: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
  /** Applies to the input. The wrapper is not stylable by design , these fields
   *  must stay rhythmically identical down a form. */
  className?: string;
  /** Escape hatch for wrapper layout (grid spans), used sparingly. */
  fieldClassName?: string;
} & Omit<React.ComponentProps<'input'>, 'children'>) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);

  return (
    <div className={cn('space-y-1.5', fieldClassName)}>
      <Label htmlFor={id} className="text-sm text-[var(--color-text-primary)]">
        {label}
        {optional ? <span className="font-normal text-[var(--color-text-secondary)]">Optional</span> : null}
      </Label>
      <div className={trailing ? 'flex items-center gap-2' : undefined}>
        {children ? (
          children({ id, describedBy, invalid })
        ) : (
          <Input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            className={cn('h-11 text-base', className)}
            {...inputProps}
          />
        )}
        {trailing}
      </div>
      {hint && !error ? (
        <p id={hintId} className="text-xs leading-relaxed text-[var(--color-text-secondary)]">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-xs leading-relaxed text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A selectable option rendered as a real radio input inside a label , so it is
 * keyboard reachable, arrow-key navigable within its group, and announced with
 * its checked state. Used wherever a guest picks one of several paths (payment
 * method, network, bank) instead of being shown every path at once.
 */
export function ChoiceCard({
  name,
  value,
  checked,
  onSelect,
  icon: Icon,
  iconNode,
  title,
  description,
  meta,
  disabled,
  className,
}: {
  name: string;
  value: string;
  checked: boolean;
  onSelect: (value: string) => void;
  icon?: LucideIcon;
  iconNode?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label
      className={cn(
        'group relative flex min-h-14 cursor-pointer items-center gap-3 rounded-[var(--radius-md)] border p-3 transition-[border-color,background-color,box-shadow] duration-150',
        checked
          ? 'border-primary bg-primary/[0.06] shadow-[0_0_0_1px_hsl(var(--primary))]'
          : 'border-[var(--cs-border)] hover:border-foreground/25 hover:bg-[var(--color-bg-sunken)]/40',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onSelect(value)}
        className="sr-only"
      />
      {iconNode ?? (Icon ? (
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-full transition-colors',
            checked ? 'bg-primary text-primary-foreground' : 'bg-[var(--color-bg-sunken)] text-[var(--color-text-secondary)]',
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </span>
      ) : null)}
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold leading-tight">{title}</span>
        {description ? (
          <span className="mt-0.5 block text-xs leading-snug text-[var(--color-text-secondary)]">{description}</span>
        ) : null}
      </span>
      {meta ? <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">{meta}</span> : null}
      <span
        aria-hidden="true"
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors',
          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-[var(--cs-border)]',
        )}
      >
        {checked ? <Check className="size-3" strokeWidth={3} /> : null}
      </span>
    </label>
  );
}

/**
 * Compact single-choice chips for short, homogeneous option sets (MoMo networks,
 * split ratios). Same accessibility contract as ChoiceCard, smaller footprint ,
 * still 44px tall so it stays thumb-friendly.
 */
export function ChoiceChip({
  name,
  value,
  checked,
  onSelect,
  children,
  disabled,
}: {
  name: string;
  value: string;
  checked: boolean;
  onSelect: (value: string) => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        'inline-flex h-11 cursor-pointer select-none items-center justify-center rounded-full border px-4 text-sm font-semibold transition-[border-color,background-color,color] duration-150',
        checked
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-[var(--cs-border)] text-[var(--color-text-primary)] hover:border-foreground/25 hover:bg-[var(--color-bg-sunken)]/50',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onSelect(value)}
        className="sr-only"
      />
      {children}
    </label>
  );
}
